import { useRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { AnonymousFigureSigil, type SeatPlayer } from './AvatarFrame.js';
import './VoterCluster.css';

/* The top-edge cluster shows up to MAX_VISIBLE voter glyphs in detail; when
   the vote count exceeds the slot budget, the last slot becomes a "+N" badge
   that condenses the remaining voters. Threshold is 4: Ghost-Meeting tops
   out at 3 voters (max 3 Ghosts per round) so overflow never fires there in
   practice, but the same component carries Discussion where every alive
   player can vote and the overflow actually engages. */
const MAX_VISIBLE = 4;

interface VoterClusterProps {
  /** Voters whose target is the cast cell this cluster belongs to. Iteration
   *  order follows the MapSchema insertion order in `state.votes` /
   *  `state.ghostMeetingVotes`, which Colyseus mirrors on the wire — i.e.
   *  by vote-arrival recency, oldest first. Typed at the seat minimum so the
   *  cluster is POV-agnostic: any phase's view-mapped player satisfies it. */
  voters: readonly SeatPlayer[];
  /** When true at mount, the voters present on the first paint skip their
   *  entry animation (reconnect / cold-arrival into a phase with votes
   *  already cast). Captured once at mount; voters arriving later animate
   *  normally. */
  justArrived?: boolean;
}

/**
 * Top-edge circular voter cluster, shared across the two voting surfaces —
 * the Ghost meeting and Discussion. Lives in the inter-row gap above the
 * tile (`position: absolute; top: -13px` relative to a `position: relative`
 * cast cell, governed by VoterCluster.css) so peak voting doesn't grow the
 * cell's vertical footprint — the slip-slot beneath the avatar is already
 * taken by the persisted mono-text clue. At 0 voters the component returns
 * null so the frame reads pristine.
 *
 * The "+N" overflow badge's reveal mechanism (popover vs. inline expansion)
 * is a craft-time follow-up; for now the badge carries a `title` attribute
 * with the names for desktop hover and an `aria-label` for screen readers.
 */
export function VoterCluster({ voters, justArrived = false }: VoterClusterProps): JSX.Element | null {
  const { t } = useTranslation();

  /* Capture the cold-arrival voter set ONCE. A glyph is suppressed iff its
     voter was present at this mount under justArrived. The attribute is then
     stable for each glyph's lifetime (initial → always 'yes'; later → always
     'no'), so no flip ever re-triggers the CSS entrance animation. */
  const suppressedIdsRef = useRef<ReadonlySet<string> | null>(null);
  if (suppressedIdsRef.current === null) {
    suppressedIdsRef.current = justArrived ? new Set(voters.map((v) => v.id)) : new Set<string>();
  }

  if (voters.length === 0) return null;

  const visible = voters.slice(0, MAX_VISIBLE);
  const overflowCount = Math.max(0, voters.length - MAX_VISIBLE);

  return (
    <span
      className="susdle-voter-cluster"
      aria-live="polite"
      aria-atomic="true"
    >
      {visible.map((voter) => (
        <VoterGlyph
          key={voter.id}
          voter={voter}
          suppressEntry={suppressedIdsRef.current!.has(voter.id)}
        />
      ))}
      {overflowCount > 0 ? (
        <OverflowBadge count={overflowCount} hidden={voters.slice(MAX_VISIBLE)} t={t} />
      ) : null}
    </span>
  );
}

function VoterGlyph({ voter, suppressEntry }: { voter: SeatPlayer; suppressEntry: boolean }): JSX.Element {
  const { t } = useTranslation();
  const label = t('voting.voterPosted', { name: voter.name });
  const tone =
    voter.isDefaultAvatar && voter.defaultAvatarIndex !== undefined
      ? `var(--susdle-seat-tone-${voter.defaultAvatarIndex})`
      : undefined;

  return (
    <span
      className="susdle-voter-cluster__glyph"
      role="img"
      title={label}
      aria-label={label}
      data-just-arrived={suppressEntry ? 'yes' : 'no'}
    >
      {voter.isDefaultAvatar ? (
        /* No-photo voter: the same woodcut bust the cast tile shows on a
           seat-tone ground — a tiny framed portrait, not a bare initial. A real
           Discord avatar (production) renders the photo branch below. */
        <span
          className="susdle-voter-cluster__figure"
          style={tone ? { backgroundColor: tone } : undefined}
        >
          <AnonymousFigureSigil />
        </span>
      ) : (
        <img
          className="susdle-voter-cluster__photo"
          src={voter.avatarUrl}
          alt=""
          loading="lazy"
          decoding="async"
        />
      )}
    </span>
  );
}

function OverflowBadge({
  count,
  hidden,
  t,
}: {
  count: number;
  hidden: readonly SeatPlayer[];
  t: ReturnType<typeof useTranslation>['t'];
}): JSX.Element {
  /* `hidden` carries the players condensed behind the +N. The popover /
     inline-expand reveal is a craft follow-up; for now the names surface in
     the title + aria-label so desktop hover and SR users can resolve "who's
     in the +N". */
  const names = hidden.map((p) => p.name).join(', ');
  const label = `${t('voting.overflowVoters', { count })}: ${names}`;

  return (
    <span
      className="susdle-voter-cluster__overflow"
      role="img"
      title={label}
      aria-label={label}
    >
      +{count}
    </span>
  );
}
